import { useState } from 'react';
import type { OwnerRezWidget, PropertyReview } from '../types';
import { useOwnerRezWidgetLoader } from '@/hooks/useOwnerRezWidgetLoader';
import OwnerRezEmbed from './OwnerRezEmbed';

type Props = {
  reviews: PropertyReview[];
  propertyName: string;
  ownerRezWidget?: OwnerRezWidget;
};

function Stars({ count }: { count: number }) {
  return (
    <span className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <i key={i} className="ri-star-fill text-accent-500 text-xs" />
      ))}
    </span>
  );
}

export default function PropertyReviews({ reviews, propertyName, ownerRezWidget }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  useOwnerRezWidgetLoader(propertyName, ownerRezWidget ? [ownerRezWidget] : []);

  if (!ownerRezWidget && reviews.length === 0) return null;

  return (
    <section className="bg-background-100/50 py-20 md:py-28 border-y border-background-200/70">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="mb-12 md:mb-16">
          <div className="text-[11px] uppercase tracking-[0.24em] text-accent-700 mb-4">
            <span className="inline-block w-5 h-px bg-accent-500 align-middle mr-3" />
            Guest reviews
          </div>
          <h2 className="font-heading text-foreground-950 text-4xl md:text-5xl leading-[1.07] tracking-[-0.01em]">
            In their own words.
          </h2>
          <p className="mt-3 text-foreground-500 text-sm">From guests who stayed at {propertyName}.</p>
        </div>

        {ownerRezWidget ? (
          <div className="rounded-2xl bg-background-50 border border-background-200/80 p-4 md:p-6 overflow-hidden">
            <OwnerRezEmbed widget={ownerRezWidget} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reviews.map((r) => {
            const isLong = r.body.length > 220;
            const isOpen = expanded === r.id;
            return (
              <div
                key={r.id}
                className="bg-background-50 border border-background-200/80 rounded-2xl p-6 flex flex-col gap-4"
              >
                <Stars count={r.stars} />
                <p className="font-heading text-xl text-foreground-950 leading-snug">{r.title}</p>
                <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                  {isLong && !isOpen ? `${r.body.slice(0, 220).trim()}\u2026` : r.body}
                </p>
                {isLong && (
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : r.id)}
                    className="text-xs text-foreground-500 hover:text-foreground-950 cursor-pointer self-start transition-colors"
                  >
                    {isOpen ? 'Show less' : 'Read more'}
                  </button>
                )}
                <div className="border-t border-background-200/70 pt-4 flex items-center justify-between">
                  <p className="text-sm text-foreground-700 font-medium">{r.author}</p>
                  <p className="text-xs text-foreground-400 uppercase tracking-[0.18em]">{r.date}</p>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>
    </section>
  );
}
